import { Box } from '@chakra-ui/react';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { postRequestNumberVerification } from 'API/TPMTrial';
import { useRequestNumber } from 'hooks/useCustomQuery/';

import {
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    LoadingIndicator,
    Text,
} from '@/components/v4';
import {
    AssociatedAccountsTable,
    TpmAssociatedNumberForm,
    TpmTrialExpired,
    useTpmActions,
    useTpmContext,
    useTpmState,
} from 'pages/tpm/';
import AssociateNumbersCard from 'pages/tpm/AssociatedAccounts/AssociateNumbersCard';

import { ERROR, FOUND, REQUEST_NUMBER } from '@/store/constants';
import { delay, MIN_REQUEST_INTERVAL } from 'pages/tpm/constants';
import walkthroughIds from './walkthroughIds';

function TpmView({ associatedAccounts = [], isLoadingState, onClose }) {
    const { tpmNumber } = useTpmContext();
    const state = useTpmState();
    const actions = useTpmActions();
    const [tpmState, setTpmState] = tpmNumber;
    const intervalRef = useRef(null);

    const dispatch = useDispatch();
    const { id } = useSelector((s) => s?.modal?.state?.service || {});
    const { darkMode } = useSelector((s) => s.settings);
    const { currentCompanyData: companyData } = useSelector(
        (s) => s.navigationLists,
    );
    const {
        number: mpn,
        error: tpmError,
        currentStep,
        operatorStatus: oStatus,
        lastUpdate,
    } = useSelector((s) => s.service.tpm);

    const { ufPhoneNumberInput, ufNextButton } =
        walkthroughIds.teamsPhoneMobileTrial;

    // Conditional variables
    const isTrialExpired =
        companyData?.isTrial &&
        new Date(companyData?.trialEnd + 'Z') < new Date();
    const hasNoAssociatedAccounts = !associatedAccounts?.length;
    const hasAssociatedNumbers =
        associatedAccounts?.length > 0 && !isTrialExpired;
    const cardBgStyle = darkMode ? 'dark.bgDarkGray' : 'white';

    // Determine if we should show the form based on screen or legacy state
    const isAssociatingAccount =
        hasNoAssociatedAccounts ||
        state.screen === 'NUMBER_INPUT' ||
        state.screen === 'LOADING' ||
        state.screen === 'VERIFICATION' ||
        state.screen === 'ERROR' ||
        currentStep !== 0;

    const _success = useCallback(() => {
        setTpmState({
            status: 'SUCCESS',
            number: tpmState.number || mpn,
        });
        actions.validationSuccess();
        dispatch({
            type: FOUND,
        });
    }, [tpmState.number, mpn, setTpmState, actions, dispatch]);

    const _error = useCallback(
        (error) => {
            const msg =
                error?.response?.data?.message ||
                'Error submitting request. Please try again.';
            setTpmState((prev) => ({
                ...prev,
                status: 'ERROR',
                number: prev.number || mpn,
            }));
            actions.validationFailure(msg);
            dispatch({
                type: ERROR,
                payload: msg,
            });
        },
        [dispatch, mpn, setTpmState, actions],
    );

    const handleSubmit = async (e) => {
        e.preventDefault();

        const phoneNumber = state.phoneNumber || tpmState.number;

        // Guard: Don't submit with empty or invalid phone number
        if (!phoneNumber || /^\d{1,4}$/.test(phoneNumber)) {
            return;
        }

        actions.startValidation(phoneNumber);
        setTpmState({
            status: 'LOADING',
            number: phoneNumber,
            error: null,
        });

        dispatch({
            type: REQUEST_NUMBER,
            payload: { number: phoneNumber },
        });

        postRequestNumberVerification({
            serviceID: id,
            phonenumber: phoneNumber,
        })
            .then(() => {
                _success();
            })
            .catch((error) => {
                _error(error);
            });
    };

    const requestNumber = useRequestNumber(id, mpn, delay);
    const mutateOptions = useCallback(
        () => ({
            onSuccess: () => {
                _success();
            },
            onError: (error) => {
                _error(error);
            },
        }),
        [_error, _success],
    );

    const startQueue = useCallback(() => {
        // Guard: Don't start queue without valid phone number
        if (!mpn || /^\d{1,4}$/.test(mpn)) {
            return;
        }

        postRequestNumberVerification({
            serviceID: id,
            phonenumber: mpn,
        })
            .then(() => {
                _success();
            })
            .catch((error) => {
                _error(error);
            });

        const now = new Date();
        const minutes = now.getMinutes();
        const seconds = now.getSeconds();
        const nextBlock = Math.ceil((minutes + 1) / 15) * 15;
        const waitTime =
            (nextBlock - minutes - 1) * 60 * 1000 + (60 - seconds) * 1000;
        setTimeout(() => {
            intervalRef.current = setInterval(
                () => {
                    requestNumber.mutate(
                        {
                            phonenumber: mpn,
                        },
                        {
                            ...mutateOptions,
                        },
                    );
                },
                15 * 60 * 1000,
            );
        }, waitTime);
    }, [id, mpn, requestNumber, _error, _success, mutateOptions]);

    useEffect(() => {
        let isMounted = true;

        const timeSinceLastRequest = Date.now() - (lastUpdate || 0);
        const canMakeRequest = timeSinceLastRequest >= MIN_REQUEST_INTERVAL;

        if (
            mpn &&
            (oStatus === 'queued' || oStatus === 'pending') &&
            isMounted &&
            !tpmError &&
            canMakeRequest
        ) {
            startQueue();
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            isMounted = false;
        };
        // NOTE: startQueue is complicated dependency that leads to infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mpn, oStatus, dispatch, lastUpdate]);

    const handleNewAccount = () => {
        actions.goToScreen('NUMBER_INPUT');
    };

    if (isLoadingState) {
        return (
            <div className="flex h-full flex-col items-center justify-center">
                <LoadingIndicator />
            </div>
        );
    }

    let tpmContent;
    if (isTrialExpired) {
        tpmContent = <TpmTrialExpired darkMode={darkMode} />;
    } else if (isAssociatingAccount) {
        tpmContent = (
            <TpmAssociatedNumberForm
                hasNoAssociatedAccounts={hasNoAssociatedAccounts}
                onClose={onClose}
                ufNextButton={ufNextButton}
                ufPhoneNumberInput={ufPhoneNumberInput}
            />
        );
    } else {
        tpmContent = (
            <>
                <CardHeader>
                    <Text className="text-xl font-semibold">
                        Managed Accounts
                    </Text>
                </CardHeader>
                <CardBody>
                    <AssociatedAccountsTable
                        data={associatedAccounts}
                        loading={isLoadingState}
                        serviceID={id}
                    />
                </CardBody>
                <CardFooter>
                    <Button
                        onClick={handleNewAccount}
                        w={{ base: 'full', md: '130px' }}
                        data-walkthroughid={ufNextButton}
                        aria-label="Add a new associated account">
                        New Account
                    </Button>
                </CardFooter>
            </>
        );
    }

    return (
        <>
            <Box as="form" onSubmit={handleSubmit}>
                <Card bg={cardBgStyle}>{tpmContent}</Card>
            </Box>
            {hasAssociatedNumbers && (
                <AssociateNumbersCard
                    associatedAccounts={associatedAccounts}
                    darkMode={darkMode}
                    onClose={onClose}
                />
            )}
        </>
    );
}

export default TpmView;
